import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { proposalService, type CreateProposalData, type UpdateProposalData } from '../services/proposalService';
import { toast } from '@/hooks/use-toast';

// Query keys factory
export const proposalKeys = {
  all: ['proposals'] as const,
  lists: () => [...proposalKeys.all, 'list'] as const,
  list: (filters: Record<string, any> = {}) => [...proposalKeys.lists(), filters] as const,
  details: () => [...proposalKeys.all, 'detail'] as const,
  detail: (id: string) => [...proposalKeys.details(), id] as const,
  stats: () => [...proposalKeys.all, 'stats'] as const,
};

// Get all proposals
export const useProposals = () => {
  return useQuery({
    queryKey: proposalKeys.lists(),
    queryFn: proposalService.getProposals,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get proposal by ID
export const useProposal = (id: string) => {
  return useQuery({
    queryKey: proposalKeys.detail(id),
    queryFn: () => proposalService.getProposalById(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id,
  });
};

// Get proposal statistics
export const useProposalStats = () => {
  return useQuery({
    queryKey: proposalKeys.stats(),
    queryFn: proposalService.getProposalStats,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Create proposal mutation
export const useCreateProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: proposalService.createProposal,
    onMutate: async (newProposal: CreateProposalData) => {
      await queryClient.cancelQueries({ queryKey: proposalKeys.lists() });

      const previousProposals = queryClient.getQueryData(proposalKeys.lists());

      queryClient.setQueryData(proposalKeys.lists(), (old: any) => [
        { ...newProposal, id: 'temp-' + Date.now(), created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
        ...(old || [])
      ]);

      return { previousProposals };
    },
    onError: (err, newProposal, context) => {
      if (context?.previousProposals) {
        queryClient.setQueryData(proposalKeys.lists(), context.previousProposals);
      }
      toast({
        title: 'Error',
        description: 'Failed to create proposal. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Proposal created successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: proposalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: proposalKeys.stats() });
    },
  });
};

// Update proposal mutation
export const useUpdateProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: proposalService.updateProposal,
    onMutate: async (updatedProposal: UpdateProposalData) => {
      await queryClient.cancelQueries({ queryKey: proposalKeys.lists() });
      await queryClient.cancelQueries({ queryKey: proposalKeys.detail(updatedProposal.id) });

      const previousProposals = queryClient.getQueryData(proposalKeys.lists());
      const previousProposal = queryClient.getQueryData(proposalKeys.detail(updatedProposal.id));

      queryClient.setQueryData(proposalKeys.lists(), (old: any) => 
        old?.map((proposal: any) => 
          proposal.id === updatedProposal.id 
            ? { ...proposal, ...updatedProposal, updated_at: new Date().toISOString() }
            : proposal
        ) || []
      );

      queryClient.setQueryData(proposalKeys.detail(updatedProposal.id), (old: any) => 
        old ? { ...old, ...updatedProposal, updated_at: new Date().toISOString() } : old
      );

      return { previousProposals, previousProposal };
    },
    onError: (err, updatedProposal, context) => {
      if (context?.previousProposals) {
        queryClient.setQueryData(proposalKeys.lists(), context.previousProposals);
      }
      if (context?.previousProposal) {
        queryClient.setQueryData(proposalKeys.detail(updatedProposal.id), context.previousProposal);
      }
      toast({
        title: 'Error',
        description: 'Failed to update proposal. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Proposal updated successfully.',
      });
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: proposalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: proposalKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: proposalKeys.stats() });
    },
  });
};

// Delete proposal mutation
export const useDeleteProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: proposalService.deleteProposal,
    onMutate: async (proposalId: string) => {
      await queryClient.cancelQueries({ queryKey: proposalKeys.lists() });

      const previousProposals = queryClient.getQueryData(proposalKeys.lists());

      queryClient.setQueryData(proposalKeys.lists(), (old: any) => 
        old?.filter((proposal: any) => proposal.id !== proposalId) || []
      );

      return { previousProposals };
    },
    onError: (err, proposalId, context) => {
      if (context?.previousProposals) {
        queryClient.setQueryData(proposalKeys.lists(), context.previousProposals);
      }
      toast({
        title: 'Error',
        description: 'Failed to delete proposal. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Proposal deleted successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: proposalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: proposalKeys.stats() });
    },
  });
};